/**
 * InterviewReadiness — foundation page for the new job-seeker interview
 * preparation experience. First pass: three module cards with starter
 * panels. No fake progress, scores, AI feedback, or mock results.
 */

import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import Layout from '@/components/Layout';
import { GraduationCap, ListChecks, MessageCircleQuestion, UserCheck, ArrowLeft, CheckCircle2, Circle, ArrowRight, Library, MessageSquare, FileText, Users, Sparkles, Save, Loader2, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MODULES = [
  {
    id: 'fundamentals',
    icon: GraduationCap,
    title: 'Interview Fundamentals',
    description: 'Understand what civilian employers listen for and how to answer with clarity.',
    cta: 'Start fundamentals',
    starter:
      'Civilian interviews reward clear, jargon-free storytelling. In this module you will learn how to translate military terms, structure concise answers, and project confidence. Full guided lessons are coming soon — for now, review the fundamentals checklist below as you prepare.',
  },
  {
    id: 'star',
    icon: ListChecks,
    title: 'STAR Story Builder',
    description: 'Turn military experience into structured stories using Situation, Task, Action, and Result.',
    cta: 'Build a STAR story',
    starter: null,
  },
  {
    id: 'practice',
    icon: MessageCircleQuestion,
    title: 'Practice Questions',
    description: 'Practice common interview questions and improve your answers over time.',
    cta: 'Start practice',
    starter:
      'Rehearsing out loud builds fluency and reduces nerves. Interactive practice with feedback is coming soon — for now, work through common questions such as "Tell me about yourself," "Why are you leaving the service?", and "Describe a time you led under pressure."',
  },
];

const STAR_PROMPTS = [
  { id: 'leadership', title: 'Leadership Challenge', description: 'A time you led people through a difficult situation.', coaching: "You've chosen a leadership story. Think about the challenge you faced before describing the actions you took." },
  { id: 'problem-solving', title: 'Problem Solving', description: 'A time you solved a difficult or unexpected problem.', coaching: "You've chosen a problem-solving story. Start by setting up the problem clearly before walking through how you tackled it." },
  { id: 'mission-success', title: 'Mission Success', description: "An accomplishment you're especially proud of.", coaching: "You've chosen a mission-success story. Frame the goal and stakes first, then show how your actions drove the result." },
  { id: 'process-improvement', title: 'Process Improvement', description: 'A time you improved efficiency, safety, or performance.', coaching: "You've chosen a process-improvement story. Describe what wasn't working before highlighting the change you made." },
  { id: 'mentoring', title: 'Mentoring Someone', description: 'A time you developed or coached another service member.', coaching: "You've chosen a mentoring story. Set up where the person started before describing how you helped them grow." },
  { id: 'decision-pressure', title: 'Decision Under Pressure', description: 'A time you had to make an important decision with limited time or information.', coaching: "You've chosen a decision-under-pressure story. Establish the constraints and stakes before explaining your decision." },
];

const STAR_FIELDS = [
  { key: 'situation', label: 'Situation', placeholder: 'Describe the situation or challenge.', tip: 'Keep this brief and establish context.' },
  { key: 'task', label: 'Task', placeholder: 'What responsibility or objective did you own?', tip: 'Explain your responsibility, not your rank.' },
  { key: 'action', label: 'Action', placeholder: 'What specific actions did you personally take?', tip: 'Focus on what you did.' },
  { key: 'result', label: 'Result', placeholder: 'What happened because of your actions?', tip: 'Emphasize measurable outcomes whenever possible.' },
];

// Local-only STAR builder. No AI, no saving, no scoring, no persistence.
// Local-only intro lesson. No persistence, no progress tracking.
function FundamentalsLesson({ onGoToStar }) {
  const checklist = [
    { label: 'Learn interview fundamentals', done: true },
    { label: 'Build several STAR stories', done: false },
    { label: 'Practice answering real interview questions', done: false },
    { label: 'Prepare for live interviews', done: false },
  ];

  return (
    <div data-testid="ir-fundamentals-lesson" className="mt-4 space-y-5 text-sm text-slate-700 leading-relaxed">
      <section>
        <h4 className="text-base font-bold text-slate-900 mb-1.5">Why employers ask behavioral questions</h4>
        <p className="mb-2">
          Employers are no longer focused only on what you did or what rank you held. They want
          evidence of how you operate — proof you can deliver in their environment.
        </p>
        <ul className="space-y-1 pl-1">
          {['lead people', 'solve problems', 'handle pressure', 'communicate clearly', 'make decisions', 'deliver results'].map((t) => (
            <li key={t} className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-brand flex-shrink-0" />
              <span className="capitalize">{t}</span>
            </li>
          ))}
        </ul>
      </section>

      <section>
        <h4 className="text-base font-bold text-slate-900 mb-1.5">Why military experience is difficult to explain</h4>
        <p>
          Veterans often have incredible experience but describe it using military language that
          civilian interviewers may not fully understand. Sergeant TAP helps translate those
          experiences into stories employers immediately recognize.
        </p>
      </section>

      <section>
        <h4 className="text-base font-bold text-slate-900 mb-1.5">What is the STAR method?</h4>
        <ul className="space-y-1 mb-2">
          <li><span className="font-semibold text-slate-900">Situation</span> — the context or challenge you faced.</li>
          <li><span className="font-semibold text-slate-900">Task</span> — the responsibility or objective you owned.</li>
          <li><span className="font-semibold text-slate-900">Action</span> — the specific steps you personally took.</li>
          <li><span className="font-semibold text-slate-900">Result</span> — the outcome your actions produced.</li>
        </ul>
        <p>
          This framework keeps your answers organized and demonstrates impact, instead of simply
          listing responsibilities.
        </p>
      </section>

      <section>
        <h4 className="text-base font-bold text-slate-900 mb-2">What you'll build</h4>
        <ul className="space-y-1.5">
          {checklist.map((item) => (
            <li key={item.label} className="flex items-start gap-2">
              {item.done ? (
                <CheckCircle2 className="w-4 h-4 text-brand flex-shrink-0 mt-0.5" />
              ) : (
                <Circle className="w-4 h-4 text-slate-300 flex-shrink-0 mt-0.5" />
              )}
              <span className={item.done ? 'text-slate-900 font-medium' : 'text-slate-500'}>{item.label}</span>
            </li>
          ))}
        </ul>
      </section>

      <div className="pt-1">
        <p className="text-sm font-semibold text-slate-900 mb-2">Ready to build your first interview story?</p>
        <Button
          data-testid="ir-fundamentals-go-star"
          onClick={onGoToStar}
          className="w-full bg-brand hover:bg-brand-hover text-white font-semibold"
        >
          Go to STAR Story Builder
          <ArrowRight className="w-4 h-4 ml-1.5" />
        </Button>
      </div>
    </div>
  );
}

const PRACTICE_QUESTIONS = [
  { id: 'pq-leadership', question: 'Tell me about a time you led through a difficult situation.', storyType: 'Leadership Challenge', tip: 'Use a story where your actions changed the outcome, not just one where you were present.' },
  { id: 'pq-problem', question: 'Tell me about a time you solved an unexpected problem.', storyType: 'Problem Solving', tip: 'Pick a problem with real stakes so your thinking and resourcefulness stand out.' },
  { id: 'pq-mission', question: "Tell me about an accomplishment you're proud of.", storyType: 'Mission Success', tip: 'Choose an achievement where your contribution was clear and measurable.' },
  { id: 'pq-process', question: 'Tell me about a time you improved a process.', storyType: 'Process Improvement', tip: 'Show the before-and-after so the impact of your change is obvious.' },
  { id: 'pq-mentoring', question: 'Tell me about a time you coached or developed someone.', storyType: 'Mentoring Someone', tip: 'Focus on how the person grew because of your guidance.' },
  { id: 'pq-decision', question: 'Tell me about a time you had to make a decision under pressure.', storyType: 'Decision Under Pressure', tip: 'Highlight the constraints you faced and why your decision was sound.' },
];

// Local-only practice guidance. No AI, no scoring, no persistence.
function PracticeQuestions({ onGoToStar }) {
  const [selected, setSelected] = useState(null);

  return (
    <div data-testid="ir-practice-questions" className="mt-4 space-y-4">
      <div>
        <h4 className="text-base font-bold text-slate-900">Practice Applying Your STAR Stories</h4>
        <p className="text-sm text-slate-500 mt-0.5 leading-relaxed">
          Civilian interviewers often ask broad behavioral questions. The goal is not to memorize
          answers — it's to recognize what kind of story the question is asking for.
        </p>
      </div>

      <div className="space-y-2.5">
        {PRACTICE_QUESTIONS.map((q) => {
          const active = selected === q.id;
          return (
            <div key={q.id}>
              <button
                type="button"
                data-testid={`ir-practice-q-${q.id}`}
                aria-pressed={active}
                onClick={() => setSelected(active ? null : q.id)}
                className={`w-full text-left p-3 rounded-xl border transition-colors ${
                  active
                    ? 'border-brand bg-brand-light ring-1 ring-brand'
                    : 'border-slate-200 bg-white hover:border-brand/40 hover:bg-brand-light/30'
                }`}
              >
                <p className={`text-sm font-semibold ${active ? 'text-brand' : 'text-slate-900'}`}>{q.question}</p>
                <p className="text-xs text-slate-500 mt-1">
                  <span className="font-semibold text-slate-700">Best story type:</span> {q.storyType}
                </p>
                <p className="text-xs text-slate-400 mt-1 leading-relaxed">{q.tip}</p>
              </button>

              {active && (
                <div
                  data-testid={`ir-practice-answer-${q.id}`}
                  className="mt-2 p-4 rounded-xl bg-brand-light/50 border border-brand/20 text-sm text-slate-700"
                >
                  <p className="text-xs font-semibold uppercase tracking-wider text-brand mb-2">How to answer this</p>
                  <ol className="list-decimal pl-5 space-y-1 mb-3">
                    <li>Start with the situation.</li>
                    <li>Explain your responsibility.</li>
                    <li>Focus on what you personally did.</li>
                    <li>End with the result or impact.</li>
                  </ol>
                  <p className="text-sm mb-2">
                    <span className="font-semibold text-slate-900">Suggested next step:</span> Use the STAR
                    Story Builder to create or refine this story.
                  </p>
                  <Button
                    data-testid={`ir-practice-go-star-${q.id}`}
                    onClick={onGoToStar}
                    className="w-full bg-brand hover:bg-brand-hover text-white font-semibold"
                  >
                    Go to STAR Story Builder
                    <ArrowRight className="w-4 h-4 ml-1.5" />
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StarStoryBuilder({ onSaved }) {
  const [story, setStory] = useState({ situation: '', task: '', action: '', result: '' });
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [saving, setSaving] = useState(false);
  const update = (key) => (e) => setStory((s) => ({ ...s, [key]: e.target.value }));
  const filled = STAR_FIELDS.filter((f) => story[f.key].trim().length > 0);
  const coaching = STAR_PROMPTS.find((p) => p.id === selectedPrompt)?.coaching;
  const allFilled = STAR_FIELDS.every((f) => story[f.key].trim().length > 0);

  const handleSave = async () => {
    if (!allFilled || saving) return;
    setSaving(true);
    try {
      const category = STAR_PROMPTS.find((p) => p.id === selectedPrompt)?.title || 'General';
      await axios.post(
        `${API}/interview-stories`,
        {
          category,
          situation: story.situation.trim(),
          task: story.task.trim(),
          action: story.action.trim(),
          result: story.result.trim(),
        },
        { withCredentials: true }
      );
      toast.success('Story saved to My Story Library');
      setStory({ situation: '', task: '', action: '', result: '' });
      setSelectedPrompt(null);
      if (typeof onSaved === 'function') onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save your story');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="ir-star-builder" className="mt-4 space-y-5">
      {/* Choose a story prompt */}
      <div data-testid="ir-star-prompts">
        <h4 className="text-base font-bold text-slate-900">Choose a story to build</h4>
        <p className="text-sm text-slate-500 mt-0.5 mb-3">
          Start with a military experience you're proud of. We'll help organize it into a strong STAR interview answer.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          {STAR_PROMPTS.map((p) => {
            const active = selectedPrompt === p.id;
            return (
              <button
                key={p.id}
                type="button"
                data-testid={`ir-star-prompt-${p.id}`}
                aria-pressed={active}
                onClick={() => setSelectedPrompt(active ? null : p.id)}
                className={`text-left p-3 rounded-xl border transition-colors ${
                  active
                    ? 'border-brand bg-brand-light ring-1 ring-brand'
                    : 'border-slate-200 bg-white hover:border-brand/40 hover:bg-brand-light/30'
                }`}
              >
                <p className={`text-sm font-semibold ${active ? 'text-brand' : 'text-slate-900'}`}>{p.title}</p>
                <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{p.description}</p>
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <h4 className="text-base font-bold text-slate-900">Build Your First STAR Story</h4>
        <p className="text-sm text-slate-500 mt-0.5">
          Turn one military experience into a clear civilian interview answer.
        </p>
      </div>

      {coaching && (
        <div
          data-testid="ir-star-coaching"
          className="p-3 rounded-lg bg-brand-light/60 border border-brand/20 text-sm text-slate-700"
        >
          {coaching}
        </div>
      )}

      <div className="space-y-4">
        {STAR_FIELDS.map((f) => (
          <div key={f.key}>
            <label className="block text-sm font-semibold text-slate-700 mb-1">{f.label}</label>
            <Textarea
              data-testid={`ir-star-${f.key}`}
              value={story[f.key]}
              onChange={update(f.key)}
              placeholder={f.placeholder}
              rows={2}
              className="resize-none"
            />
            <p className="text-xs text-slate-400 mt-1">{f.tip}</p>
          </div>
        ))}
      </div>

      <div
        data-testid="ir-star-preview"
        className="p-4 rounded-xl bg-brand-light/50 border border-brand/20"
      >
        <p className="text-xs font-semibold uppercase tracking-wider text-brand mb-2">Your STAR Story</p>
        {filled.length === 0 ? (
          <p className="text-sm text-slate-400">Start filling in the fields above to see your story take shape.</p>
        ) : (
          <div className="space-y-2 text-sm text-slate-700 leading-relaxed">
            {filled.map((f) => (
              <p key={f.key}>
                <span className="font-semibold text-slate-900">{f.label}:</span> {story[f.key].trim()}
              </p>
            ))}
          </div>
        )}
      </div>

      <div>
        <Button
          data-testid="ir-star-save"
          onClick={handleSave}
          disabled={!allFilled || saving}
          className="w-full bg-brand hover:bg-brand-hover text-white font-semibold disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
          Save to My Story Library
        </Button>
        {!allFilled && (
          <p className="text-xs text-slate-400 mt-1.5 text-center">
            Fill in Situation, Task, Action, and Result to save.
          </p>
        )}
      </div>
    </div>
  );
}

const LIBRARY_CHIPS = ['Leadership', 'Problem Solving', 'Mission Success', 'Process Improvement', 'Mentoring', 'Decision Making'];

const COMING_SOON = [
  { icon: MessageCircleQuestion, title: 'Interview Preparation', description: 'Reuse your strongest stories for behavioral interview questions.' },
  { icon: FileText, title: 'Resume Improvements', description: 'Turn accomplishments into stronger resume bullets.' },
  { icon: Users, title: 'Networking', description: 'Recall examples quickly during conversations.' },
  { icon: Sparkles, title: 'AI Interview Coaching', description: 'Receive personalized coaching using your own experiences.' },
];

// Saved-story persistence wired via /api/interview-stories (per-user).
function StoryLibrarySection({ stories = [], onDeleted }) {
  const [deletingId, setDeletingId] = useState(null);
  const hasStories = Array.isArray(stories) && stories.length > 0;

  const handleDelete = async (storyId) => {
    setDeletingId(storyId);
    try {
      await axios.delete(`${API}/interview-stories/${storyId}`, { withCredentials: true });
      toast.success('Story removed');
      if (typeof onDeleted === 'function') onDeleted();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not remove story');
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  return (
    <section data-testid="ir-story-library" className="mt-12">
      <h2 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">My Story Library</h2>
      <p className="text-sm sm:text-base text-slate-500 mt-1 max-w-3xl leading-relaxed">
        Every STAR story you create becomes part of your personal interview toolkit. Over time you'll
        build examples that demonstrate leadership, problem solving, decision making, mentoring, and
        many other strengths.
      </p>

      {hasStories ? (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="ir-library-list">
          {stories.map((s) => (
            <Card
              key={s.story_id}
              data-testid={`ir-library-story-${s.story_id}`}
              className="p-5 border border-slate-200 shadow-sm rounded-2xl"
            >
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="min-w-0">
                  <h3 className="text-base font-bold text-slate-900 truncate">{s.title}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-brand-light text-brand border border-brand/15">
                      {s.category}
                    </span>
                    <span className="text-xs text-slate-400">{formatDate(s.created_at)}</span>
                  </div>
                </div>
                <button
                  type="button"
                  data-testid={`ir-library-delete-${s.story_id}`}
                  onClick={() => handleDelete(s.story_id)}
                  disabled={deletingId === s.story_id}
                  className="text-slate-300 hover:text-red-500 transition-colors flex-shrink-0"
                  aria-label="Delete story"
                >
                  {deletingId === s.story_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                </button>
              </div>
              <div className="space-y-1 text-xs text-slate-600 leading-relaxed">
                <p className="line-clamp-2"><span className="font-semibold text-slate-700">Situation:</span> {s.situation}</p>
                <p className="line-clamp-1"><span className="font-semibold text-slate-700">Task:</span> {s.task}</p>
                <p className="line-clamp-1"><span className="font-semibold text-slate-700">Action:</span> {s.action}</p>
                <p className="line-clamp-1"><span className="font-semibold text-slate-700">Result:</span> {s.result}</p>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        /* Empty state */
        <Card
          data-testid="ir-library-empty-state"
          className="mt-6 p-8 border border-dashed border-slate-300 rounded-2xl bg-white text-center flex flex-col items-center"
        >
          <div className="w-14 h-14 rounded-2xl bg-brand-light flex items-center justify-center mb-4">
            <Library className="w-7 h-7 text-brand" strokeWidth={1.6} />
          </div>
          <h3 className="text-lg font-bold text-slate-900">Your story library is empty</h3>
          <p className="text-sm text-slate-500 mt-1.5 max-w-xl leading-relaxed">
            As you build STAR stories, they'll eventually appear here so you can reuse them for
            interviews, resumes, networking conversations, and future opportunities.
          </p>
          <div className="mt-5 flex flex-wrap justify-center gap-2">
            {LIBRARY_CHIPS.map((chip) => (
              <span
                key={chip}
                data-testid={`ir-library-chip-${chip.toLowerCase().replace(/\s+/g, '-')}`}
                className="px-3 py-1 rounded-full text-xs font-medium bg-brand-light text-brand border border-brand/15"
              >
                {chip}
              </span>
            ))}
          </div>
        </Card>
      )}

      {/* Coming Soon */}
      <div className="mt-8">
        <h3 className="text-base font-bold text-slate-900 mb-4">Coming Soon</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {COMING_SOON.map((c) => {
            const Icon = c.icon;
            return (
              <div
                key={c.title}
                data-testid={`ir-coming-soon-${c.title.toLowerCase().replace(/\s+/g, '-')}`}
                className="p-5 border border-slate-200 rounded-2xl bg-white"
              >
                <div className="w-10 h-10 rounded-xl bg-brand-light flex items-center justify-center mb-3">
                  <Icon className="w-5 h-5 text-brand" strokeWidth={1.8} />
                </div>
                <h4 className="text-sm font-bold text-slate-900 mb-1">{c.title}</h4>
                <p className="text-xs text-slate-500 leading-relaxed">{c.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function InterviewReadiness() {
  const [user, setUser] = useState(null);
  const [activeModule, setActiveModule] = useState(null);
  const navigate = useNavigate();
  const [stories, setStories] = useState([]);

  const fetchStories = () => {
    axios
      .get(`${API}/interview-stories`, { withCredentials: true })
      .then((res) => setStories(Array.isArray(res.data) ? res.data : []))
      .catch(() => {});
  };

  useEffect(() => {
    axios
      .get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true })
      .then((res) => setUser(res.data))
      .catch(() => {});
    fetchStories();
  }, []);

  const goToStar = () => {
    setActiveModule('star');
    setTimeout(() => {
      document
        .querySelector('[data-testid="ir-module-star"]')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 90);
  };

  return (
    <Layout user={user}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="interview-readiness-page">
        {/* Heading */}
        <button
          data-testid="ir-back-to-dashboard"
          onClick={() => navigate('/dashboard')}
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-brand mb-5"
        >
          <ArrowLeft className="w-4 h-4" /> Back to My Transition
        </button>

        <div className="mb-8 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-brand-light flex items-center justify-center flex-shrink-0">
            <UserCheck className="w-6 h-6 text-brand" strokeWidth={1.8} />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
              Interview Readiness
            </h1>
            <p className="text-sm sm:text-base text-slate-500 mt-0.5 max-w-2xl">
              Learn how civilian interviews work, build strong STAR stories, and practice
              translating your military experience.
            </p>
          </div>
        </div>

        {/* Modules */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
          {MODULES.map((m) => {
            const Icon = m.icon;
            const open = activeModule === m.id;
            return (
              <Card
                key={m.id}
                data-testid={`ir-module-${m.id}`}
                className="p-6 border border-slate-200 shadow-sm rounded-2xl flex flex-col"
              >
                <div className="w-12 h-12 rounded-xl bg-brand-light flex items-center justify-center mb-4">
                  <Icon className="w-6 h-6 text-brand" strokeWidth={1.8} />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{m.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed mb-5 flex-1">{m.description}</p>
                <Button
                  data-testid={`ir-cta-${m.id}`}
                  onClick={() => setActiveModule(open ? null : m.id)}
                  variant="outline"
                  className="w-full border-brand text-brand hover:bg-brand hover:text-white font-semibold"
                >
                  {open ? 'Hide' : m.cta}
                </Button>
                {open && m.id === 'fundamentals' && <FundamentalsLesson onGoToStar={goToStar} />}
                {open && m.id === 'star' && <StarStoryBuilder onSaved={fetchStories} />}
                {open && m.id === 'practice' && <PracticeQuestions onGoToStar={goToStar} />}
              </Card>
            );
          })}
        </div>

        <StoryLibrarySection stories={stories} onDeleted={fetchStories} />
      </div>
    </Layout>
  );
}

export default InterviewReadiness;
